// ===========================================
// Disable Two-Factor Dialog Component
// ===========================================
// Destructive confirmation requiring password + code

import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  TextField,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useDisable2FA } from '../../../hooks/useTwoFactor';

interface Props {
  open: boolean;
  onClose: () => void;
}

export default function DisableTwoFactorDialog({ open, onClose }: Props) {
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const disable2FA = useDisable2FA();

  const handleDisable = async () => {
    try {
      await disable2FA.mutateAsync({ password, code });
      handleClose();
    } catch {
      // Error handled by hook's onError
    }
  };

  const handleClose = () => {
    setPassword('');
    setCode('');
    setShowPassword(false);
    disable2FA.reset();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Disable Two-Factor Authentication</DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Disabling two-factor authentication will reduce the security of your account.
          You will only need your password to sign in.
        </Alert>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            autoFocus
            fullWidth
            label="Password"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                    size="small"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            fullWidth
            label="Authentication Code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="6-digit code or backup code"
            helperText="Enter a code from your authenticator app or a backup code"
            error={disable2FA.isError}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleDisable}
          disabled={!password || !code || disable2FA.isPending}
        >
          {disable2FA.isPending ? 'Disabling...' : 'Disable 2FA'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
